/** @jsxImportSource ./simboluri-jsx */
// Pasul 7 — modelul FEM: pagina și vederea ei. Scena 3D, panoul de proprietăți,
// formele modale, verificările și fundația în model stau în `fem/`, iar
// ajutătoarele pure pe model în `fem/model.ts`; numele exportate de aici înainte
// se re-exportă, ca importatorii să nu se schimbe.
import { useState } from "react";
import { ClasaElement } from "./claseElemente";
import { areBrate } from "./axaGrinzii";
import { FemResult, MasaNivel, StepState, fmtDate } from "./tipuri";
import Info from "./Info";
import { numar, t, useT } from "../i18n";
import ModeleRigiditate from "./ModeleRigiditate";
import { Focus } from "./legaturi";
import { DescriereaPasului } from "./TitluPas";
import Marime from "./Marime";
import NotePas from "./NotePas";
import DeciziiPas from "./DeciziiPas";
import { FormeModale } from "./fem/FormeModale";
import { FundatiaInModel } from "./fem/FundatiaInModel";
import { PanouProprietati } from "./fem/PanouProprietati";
import { Scene3D } from "./fem/Scene3D";
import { VerificareModala, VerificariFem } from "./fem/VerificariFem";
import { CULORI_CSS, nr } from "./fem/model";
import type { Selectie } from "./fem/model";
export { CULORI, CULORI_CSS, fundareaDesenata, goluriFlat, nucleeFlat, parapeteFlat, pereteTurnatIn, placaPeSolDesenata, radiereDesenate, turnatDinPerete, zidarieFlat } from "./fem/model";
export type { SelectieFem } from "./fem/model";
export { FormeModale } from "./fem/FormeModale";
export { Scene3D } from "./fem/Scene3D";
export { PanouProprietati } from "./fem/PanouProprietati";

export default function StepFem({
  projectId,
  cloneId,
  state,
  running,
  focus,
  schita,
  schitaSeConstruieste,
  clase,
}: {
  projectId: string;
  /** soluția vizualizată, de unde se cer formele modale; null = proiectul */
  cloneId?: string | null;
  state: StepState | null;
  running: boolean;
  // verificarea modală cerută dintr-un link (?focus=modal_decuplare) — gate-ul
  // e al ANSAMBLULUI, deci nu există element de selectat: se derulează la
  // secțiunea de verificări. V. legaturi.ts.
  focus?: Focus;
  /** modelul CONSTRUIT dar NEANALIZAT, când pasul n-a rulat: geometria din
   *  Pasul 4, ca structura să se poată vedea oricum (v. `schitaFem.ts`) */
  schita?: FemResult | null;
  /** clasa de execuție a fiecărui element, după id-ul lui de plan (v.
   *  `claseElemente.ts`): la click se vede din ce tip face parte piesa */
  clase?: Record<string, ClasaElement>;
  /** vederea de mai sus se construiește chiar acum pe server */
  schitaSeConstruieste?: boolean;
}) {
  // Hook-ul, nu doar `t`-ul de modul: el e ce redesenează ecranul la
  // schimbarea limbii. Textele din funcțiile simple de mai jos merg pe `t`.
  useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.fem.ruleaza")}</strong>
          <div className="sg-muted">{t("struct.fem.ruleaza_sub")}</div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div>
        <div className="sg-muted st-empty">
          {t("struct.fem.nerulat")}
          {/* descrierea vine din catalog_pasi.py — același text cu
              cel de la hover pe titlul pasului */}
          <DescriereaPasului cheie="model_fem" />
        </div>
        {/* …dar MODELUL se poate vedea: geometria lui e gata din Pasul 4, iar
            serverul o construiește fără s-o analizeze. Ce lipsește sunt
            cifrele — perioade, mase participante, verificarea modală —, iar
            cardurile de mai jos o spun singure («verificarea modală nu a
            rulat»). V. `schitaFem.ts`. */}
        {schita?.model ? (
          <FemView r={schita} updatedAt="" focus={focus} schita clase={clase}
                   projectId={projectId} cloneId={cloneId} />
        ) : schitaSeConstruieste ? (
          <div className="st-status">
            <div className="st-spinner" />
            <div>
              <strong>{t("struct.fem.schita_titlu")}</strong>
              <div className="sg-muted">{t("struct.fem.schita_sub")}</div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const r = state.result as unknown as FemResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.fem.esec",
             { motiv: r?.error ?? t("struct.fem.eroare_necunoscuta") })}
        </div>
        <div className="sg-muted">{t("struct.fem.reruleaza")}</div>
      </div>
    );
  }

  return <FemView r={r} updatedAt={state.updated_at} focus={focus} clase={clase}
                  projectId={projectId} cloneId={cloneId} />;
}

// ---------------------------------------------------------------------------
// Vizualizarea propriu-zisă (rezultat valid)
// ---------------------------------------------------------------------------

function FemView({ r, updatedAt, focus, projectId, cloneId, schita, clase }: {
  r: FemResult;
  updatedAt: string;
  /** modelul e CONSTRUIT, nu analizat (Pasul 7 n-a rulat): nu există forme
   *  modale de cerut și nu există o oră a rulării */
  schita?: boolean;
  focus?: Focus;
  // ca panoul grinzii de fundare să poată trimite la pagina care o armează
  projectId?: string;
  // de unde se cer formele modale (v. `FormeModale`)
  cloneId?: string | null;
  /** clasa de execuție a fiecărui element, după id-ul lui de plan */
  clase?: Record<string, ClasaElement>;
}) {
  useT();
  const [sel, setSel] = useState<Selectie>(null);
  const v = r.verificare_modala;
  // modul fundamental = primul mod cu masă orizontală semnificativă
  // (la varianta shell pot exista moduri locale cu masă neglijabilă înainte)
  const fundamental =
    v?.moduri?.find((m) => m.mod === v.mod_fundamental) ?? v?.moduri?.[0];

  return (
    <div className="st-result">
      <ModeleRigiditate pas={7} r={r} />
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.fem.t1")}
            <Info
              titlu={t("struct.fem.t1_titlu")}
              ce={t("struct.fem.t1_ce")}
              deCe={t("struct.fem.t1_dece")}
              parametri={[
                { simbol: "0,5·EI / 0,7·EI", nume: t("struct.fem.t1_ei"),
                  sursa: t("struct.fem.t1_ei_sursa") },
                { simbol: "M", nume: t("struct.fem.t1_m"),
                  sursa: t("struct.fem.t1_m_sursa") },
              ]}
            />
          </div>
          <div className="st-card-value">
            {fundamental ? `${fundamental.T_s} s` : "—"}
          </div>
          <div className="st-card-sub">
            {fundamental
              ? t("struct.fem.t1_sub", { f: fundamental.f_hz,
                                         mod: fundamental.mod,
                                         total: String(v?.nr_moduri ?? "") })
                + (fundamental.mod !== 1 ? t("struct.fem.t1_sub_local") : "")
              : v?.eroare ?? t("struct.fem.modal_nerulat")}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.fem.masa")}
            <Info
              titlu={t("struct.fem.masa_titlu")}
              ce={t("struct.fem.masa_ce")}
              deCe={t("struct.fem.masa_dece")}
              parametri={[
                { simbol: "D", nume: t("struct.fem.masa_d"),
                  sursa: t("struct.fem.masa_d_sursa") },
                { simbol: "SDL", nume: t("struct.fem.masa_sdl"),
                  sursa: t("struct.fem.masa_sdl_sursa") },
                { simbol: "ψE", nume: t("struct.fem.masa_psi"),
                  sursa: t("struct.fem.masa_psi_sursa") },
                { simbol: "g", nume: t("struct.fem.masa_g"),
                  sursa: t("struct.fem.masa_g_sursa") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.masa_totala_t} t</div>
          <div className="st-card-sub">
            {t("struct.fem.masa_sub", { kn: r.greutate_seismica_kn,
                                        psi: r.parametri.psi_e })}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.fem.varianta",
               { varianta: r.parametri.varianta_model ?? "lean" })}
            <Marime cheie="varianta_model" />
          </div>
          <div className="st-card-value">
            {r.parametri.varianta_model === "shell"
              ? `${(r.nr_shells ?? 0) + (r.nr_shells_pereti ?? 0)} + ${r.nr_elemente}`
              : r.nr_elemente}
          </div>
          <div className="st-card-sub">
            {r.parametri.varianta_model === "shell"
              ? t("struct.fem.sub_shell", {
                  mesh: String(r.parametri.h_mesh_efectiv_m ?? ""),
                  ochiuri: r.nr_panouri ?? 0,
                  fasii: r.nr_shells_pereti
                    ? t("struct.fem.sub_fasii", { n: r.nr_shells_pereti }) : "",
                  noduri: r.nr_noduri })
              : t("struct.fem.sub_lean", {
                  noduri: r.nr_noduri, puncte: r.nr_noduri_fem,
                  diafragme: r.model.diafragme?.length ?? 0 })}
          </div>
        </div>
        {r.parametri.varianta_model === "shell" && (
          <div className="st-card">
            <div className="st-card-label">
              {t("struct.fem.acoperire")}
              <Info
                titlu={t("struct.fem.acoperire_titlu")}
                ce={t("struct.fem.acoperire_ce")}
                deCe={t("struct.fem.acoperire_dece")}
                parametri={[
                  { simbol: t("struct.fem.acoperire_amprenta"),
                    nume: t("struct.fem.acoperire_amprenta_nume"),
                    sursa: t("struct.fem.acoperire_amprenta_sursa") },
                  { simbol: t("struct.fem.acoperire_stalpi"),
                    nume: t("struct.fem.acoperire_stalpi_nume"),
                    sursa: t("struct.fem.acoperire_stalpi_sursa") },
                ]}
              />
            </div>
            <div className="st-card-value">
              {r.sanity?.acoperire_amprenta_pct ?? "—"}
              {r.sanity?.acoperire_amprenta_pct != null ? " %" : ""}
            </div>
            <div className="st-card-sub">
              {t("struct.fem.acoperire_sub", {
                placa: r.sanity?.arie_poligon_m2 ?? "—",
                construit: r.sanity?.arie_amprenta_m2 || "—",
                hull: r.sanity?.acoperire_hull_pct ?? "—",
                mesh: r.sanity?.acoperire_mesh_pct ?? "—" })}
            </div>
          </div>
        )}
        <div className="st-card">
          <div className="st-card-label">
            {t("struct.fem.intrari")}
            <Info
              titlu={t("struct.fem.intrari_titlu")}
              ce={t("struct.fem.intrari_ce")}
              parametri={[
                { simbol: t("struct.fem.simb_geometrie"), nume: t("struct.fem.intrari_geom"),
                  sursa: t("struct.fem.intrari_geom_sursa") },
                { simbol: t("struct.fem.simb_h_etaje"), nume: t("struct.fem.intrari_h"),
                  sursa: t("struct.fem.intrari_h_sursa") },
                { simbol: t("struct.fem.simb_beton_e"), nume: t("struct.fem.intrari_beton"),
                  sursa: t("struct.fem.intrari_beton_sursa") },
                { simbol: t("fatada.finisaj"), nume: t("struct.fem.intrari_finisaj"),
                  sursa: t("struct.fem.intrari_finisaj_sursa") },
                { simbol: t("struct.fem.simb_destinatie"), nume: t("struct.fem.intrari_dest"),
                  sursa: t("struct.fem.intrari_dest_sursa") },
                { simbol: t("struct.col.placa"), nume: t("struct.fem.intrari_placa"),
                  sursa: t("struct.fem.intrari_placa_sursa") },
              ]}
            />
          </div>
          <div className="st-card-value" style={{ fontSize: 22 }}>{r.parametri.beton}</div>
          <div className="st-card-sub">
            {t("struct.fem.intrari_sub", {
              e: r.parametri.E_gpa, finisaj: r.parametri.finisaj,
              sdl: r.parametri.sdl_kn_m2, destinatie: r.parametri.destinatie,
              q: r.parametri.q_kn_m2,
              placa: (r.parametri.grosime_placa_m * 100).toFixed(0) })}
            {r.parametri.inaltimi_etaj_m?.length
              ? t("struct.fem.intrari_etaje",
                  { lista: r.parametri.inaltimi_etaj_m.join(" / ") })
              : ""}
          </div>
        </div>
      </div>

      <VerificariFem r={r} focus={focus} />

      <SubsolRigidCard r={r} />

      <MaseConsiderate r={r} />

      <FundatiaInModel r={r} />

      <DeciziiPas decizii={r.decizii} titlu="struct.decizii.pas7" />

      <NotePas
        note={r.avertismente}
        titlu="struct.note.modelului"
        ce="struct.note.modelului_ce"
      />

      <div className="st-findings">
        <h3>
          {t("struct.fem.scena")}
          <span className="sg-muted" style={{ fontWeight: 400, fontSize: 13, marginLeft: 10 }}>
            {t("struct.fem.scena_sub")}
          </span>
        </h3>
        <div className="fem-legend md-legend">
          <span><span className="md-swatch" style={{ background: CULORI_CSS.stalp }} />{" "}
            {t("struct.fem.leg_stalpi")}</span>
          <span><span className="md-swatch" style={{ background: CULORI_CSS.grinda }} />{" "}
            {t(r.parametri.varianta_model === "shell"
              ? "struct.fem.leg_grinzi_inima" : "struct.fem.leg_grinzi_t")}</span>
          {r.model.grinzi.some(areBrate) && (
            <span><span className="md-swatch" style={{
              background: `repeating-linear-gradient(90deg, ${CULORI_CSS.grinda} 0 4px, transparent 4px 7px)`,
            }} />{" "}
              {t("struct.fem.leg_brate")}</span>
          )}
          <span><span className="md-swatch" style={{ background: CULORI_CSS.placa }} />{" "}
            {t(r.parametri.varianta_model === "shell"
              ? "struct.fem.leg_placa_mesh" : "struct.fem.leg_placa_diafragma")}</span>
          <span><span className="md-swatch" style={{ background: CULORI_CSS.nodFem }} />{" "}
            {t("struct.fem.leg_noduri")}</span>
          {(r.model.pereti_beton?.length ?? 0) > 0 && (
            <span><span className="md-swatch" style={{ background: CULORI_CSS.pereteBeton }} />{" "}
              {t("struct.fem.leg_pereti_beton")}</span>
          )}
          {r.model.etaje.some((et) => et.balcoane?.some(
            (b) => b.tip === "balcon" || b.tip === "balcon_structura")) && (
            <span><span className="md-swatch" style={{ background: CULORI_CSS.balconBeton }} />{" "}
              {t("struct.fem.leg_balcoane")}</span>
          )}
          {r.model.etaje.some((et) => et.balcoane?.some((b) => b.tip === "logie")) && (
            <span><span className="md-swatch" style={{ background: CULORI_CSS.logie }} />{" "}
              {t("struct.fem.leg_logii")}</span>
          )}
          {r.model.etaje.some((et) => et.nuclee?.some((n) => n.kind === "scara")) && (
            <span><span className="md-swatch" style={{ background: CULORI_CSS.scara }} />{" "}
              {t("struct.fem.leg_scara")}</span>
          )}
          {r.model.etaje.some((et) => et.nuclee?.some((n) => n.kind === "lift")) && (
            <span><span className="md-swatch" style={{ background: CULORI_CSS.lift }} />{" "}
              {t("struct.fem.leg_lift")}</span>
          )}
          {r.parametri.varianta_model !== "shell" && (
            <>
              <span><span className="md-swatch" style={{ background: CULORI_CSS.nodReazem }} />{" "}
                {t("struct.fem.leg_reazem")}</span>
              <span><span className="md-swatch" style={{ background: CULORI_CSS.masa }} />{" "}
                {t("struct.fem.leg_cm")}</span>
            </>
          )}
        </div>
        <div className="fem-wrap">
          <Scene3D r={r} sel={sel} onSel={setSel} noduri
                   projectId={projectId} cloneId={cloneId ?? null}
                   versiune={updatedAt} />
          <PanouProprietati r={r} sel={sel} clase={clase} onClose={() => setSel(null)}
                            projectId={projectId} />
        </div>
      </div>

      <VerificareModala r={r} />

      {!schita && (
        <FormeModale r={r} projectId={projectId} cloneId={cloneId}
                     versiune={updatedAt} />
      )}

      <div className="sg-muted st-ran-at">
        {schita
          ? t("struct.fem.schita_nota")
          : t("struct.fem.rulat_la", { data: fmtDate(updatedAt) })}
      </div>
    </div>
  );
}

function SubsolRigidCard({ r }: { r: FemResult }) {
  useT();
  const sr = r.model.subsol_rigid;
  if (!sr || !sr.cerut) return null;
  const trece = sr.trece === true;
  return (
    <div className="st-findings">
      <h3>
        {t("struct.fem.subsol")}
        <Info
          titlu={t("struct.fem.subsol_titlu")}
          ce={t("struct.fem.subsol_ce")}
          deCe={t("struct.fem.subsol_dece")}
        />
      </h3>
      <div className="sg-muted" style={{ marginBottom: 8 }}>
        {sr.motiv} · {t("struct.fem.subsol_pereti", { n: sr.nr_pereti })}
        {sr.grosime_m
          ? t("struct.fem.subsol_grosime",
              { cm: (sr.grosime_m * 100).toFixed(0) })
          : ""}
      </div>
      {sr.raport_x != null && (
        <div style={{ marginBottom: 8 }}>
          <span className={trece ? "st-ok" : "st-warn"}>
            {trece ? "✓" : "✗"}{" "}
            {t("struct.fem.subsol_raport",
               { x: nr(sr.raport_x, 1), y: nr(sr.raport_y, 1) })}{" "}
            {t(trece ? "struct.fem.subsol_prag_ok" : "struct.fem.subsol_prag_nu",
               { prag: nr(sr.prag, 1) })}
          </span>
        </div>
      )}
      {sr.niveluri?.length > 0 && (
        <table className="sg-table" style={{ maxWidth: 560 }}>
          <thead>
            <tr>
              <th>{t("struct.fem.subsol_nivel")}</th>
              <th>h [m]</th>
              <th>k_x [kN/m]</th>
              <th>k_y [kN/m]</th>
            </tr>
          </thead>
          <tbody>
            {sr.niveluri.map((n, i) => (
              <tr key={i} style={n.subteran ? { fontWeight: 600 } : undefined}>
                <td>{n.nivel}</td>
                <td>{n.h_m}</td>
                <td>{numar(Math.round(n.k_x_kn_m))}</td>
                <td>{numar(Math.round(n.k_y_kn_m))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="sg-muted" style={{ marginTop: 6, fontSize: 13 }}>
        {t("struct.fem.subsol_nota")}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Masele considerate: detaliul maselor seismice per nivel (tone)
// ---------------------------------------------------------------------------

function MaseConsiderate({ r }: { r: FemResult }) {
  useT();
  const rows = r.mase_detaliu;
  if (!rows?.length) return null;

  const sum = (get: (m: MasaNivel) => number | undefined) =>
    rows.reduce((acc, m) => acc + (get(m) ?? 0), 0);

  // [antet, extragere valoare, "—" în loc de 0 când lipsește]
  const coloane: [string, (m: MasaNivel) => number | undefined, boolean][] = [
    [t("struct.fem.mc_col_placa"), (m) => m.placa_t, false],
    [t("struct.fem.mc_col_grinzi"), (m) => m.grinzi_t, false],
    [t("struct.fem.mc_col_stalpi"), (m) => m.stalpi_t, false],
    [t("struct.fem.mc_col_pereti_grinzi"), (m) => m.pereti_grinzi_t, false],
    [t("struct.fem.mc_col_pereti_placa"), (m) => m.pereti_placa_t, false],
    [t("struct.fem.mc_col_finisaje"), (m) => m.finisaje_t, false],
    [t("struct.fem.mc_col_utila"), (m) => m.utila_t, false],
    [t("struct.fem.mc_col_zapada"), (m) => m.zapada_t, true], // placa de sub cer
    [t("struct.fem.mc_col_total"), (m) => m.total_t, false],
  ];

  return (
    <div className="st-findings">
      <h3>
        {t("struct.fem.mc_titlu")}
        <Info
          titlu={t("struct.fem.mc_info_titlu")}
          ce={t("struct.fem.mc_info_ce")}
        />
      </h3>
      <table className="sg-table fem-modes">
        <thead>
          <tr>
            <th>{t("struct.fem.mc_nivel")}</th>
            {coloane.map(([nume]) => (
              <th key={nume}>{nume}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((m, i) => (
            <tr key={i}>
              <td>{m.nivel ?? (m.nivel_diafragma != null
                ? t("struct.fem.mc_nivel_diafragma",
                    { n: m.nivel_diafragma }) : "—")}</td>
              {coloane.map(([nume, get, liniuta]) => {
                const val = get(m);
                return (
                  <td key={nume} className="sg-mono">
                    {val == null && liniuta ? "—" : (val ?? 0).toFixed(1)}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr style={{ fontWeight: 600 }}>
            <td>TOTAL</td>
            {coloane.map(([nume, get]) => (
              <td key={nume} className="sg-mono">
                {sum(get).toFixed(1)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
